import { useState, useRef, useCallback } from "react";
import { TodoItem, Project } from "../../../lib/adhd-todo-manager";
import { overwhelmDetector, OverwhelmAssessment } from "../../../lib/overwhelm-detector";
import { energyContextService, EnergyLevel } from "../../../lib/energy-context-service";
import { useTimeoutCleanup } from "../../../hooks/use-cleanup";
import { useSuccessToast } from "../../toast-notification";
import { FunctionCallProcessor } from "../services/FunctionCallProcessor";

export interface OverwhelmHookReturn {
  overwhelmAssessment: OverwhelmAssessment | null;
  recentUserMessages: string[];
  checkForOverwhelm: (userMessage?: string) => void;
  setOverwhelmAssessment: (assessment: OverwhelmAssessment | null) => void;
  setRecentUserMessages: (messages: string[]) => void;
}

export const useOverwhelm = (
  todosRef: React.RefObject<TodoItem[]>,
  projectsRef: React.RefObject<Project[]>,
  currentEnergyLevel: EnergyLevel,
  functionCallProcessor: FunctionCallProcessor | null,
  setAiPersonalityMode: (mode: "supportive" | "focused" | "calm") => void
): OverwhelmHookReturn => {
  const [overwhelmAssessment, setOverwhelmAssessment] = useState<OverwhelmAssessment | null>(null);
  const [recentUserMessages, setRecentUserMessages] = useState<string[]>([]);

  const overwhelmCheckTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const recentUserMessagesRef = useRef(recentUserMessages);

  const { setTimeout: safeSetTimeout, clearTimeout: safeClearTimeout } = useTimeoutCleanup();
  const showSuccess = useSuccessToast();

  // Keep refs updated
  recentUserMessagesRef.current = recentUserMessages;

  const handleAutomaticFunctionCall = useCallback(
    (functionCall: { name: string; args?: any }, checkOverwhelmCallback?: () => void) => {
      if (functionCallProcessor) {
        functionCallProcessor.handleAutomaticFunctionCall(functionCall, checkOverwhelmCallback);
      }
    },
    [functionCallProcessor]
  );

  // ADHD-aware overwhelm detection and response
  const checkForOverwhelm = useCallback(
    (userMessage?: string) => {
      // Clear existing timeout
      if (overwhelmCheckTimeoutRef.current) {
        safeClearTimeout(overwhelmCheckTimeoutRef.current);
        overwhelmCheckTimeoutRef.current = null;
      }

      // Add user message to recent messages if provided
      if (userMessage) {
        setRecentUserMessages(prev => {
          const updated = [userMessage, ...prev].slice(0, 5); // Keep last 5 messages

          // Schedule overwhelm assessment after brief delay
          overwhelmCheckTimeoutRef.current = safeSetTimeout(() => {
            const currentEnergy =
              energyContextService.getCurrentContext()?.energyLevel || currentEnergyLevel;

            // Use current state values instead of stale closure values
            setOverwhelmAssessment(currentAssessment => {
              const assessment = overwhelmDetector.assessOverwhelm(
                todosRef.current || [],
                projectsRef.current || [],
                updated,
                currentEnergy
              );

              // Auto-apply overwhelm interventions for severe cases
              if (assessment.overallLevel === "severe") {
                const intervention = overwhelmDetector.getOverwhelmIntervention(assessment);

                // Apply automatic interventions
                intervention.actions.forEach(action => {
                  if (action.functionCall) {
                    try {
                      handleAutomaticFunctionCall(action.functionCall, () => checkForOverwhelm());
                    } catch (error) {
                      console.error("Failed to execute overwhelm intervention:", error);
                    }
                  }
                });

                // Send supportive message to AI
                setAiPersonalityMode("calm");
                showSuccess("Overwhelm Support", intervention.message);
              }

              return assessment;
            });
          }, 1000); // 1 second delay to avoid too frequent assessments

          return updated;
        });
      } else {
        // Just assess current state without new message
        const currentEnergy =
          energyContextService.getCurrentContext()?.energyLevel || currentEnergyLevel;

        setOverwhelmAssessment(currentAssessment => {
          const assessment = overwhelmDetector.assessOverwhelm(
            todosRef.current || [],
            projectsRef.current || [],
            recentUserMessagesRef.current,
            currentEnergy
          );
          return assessment;
        });
      }
    },
    [
      currentEnergyLevel,
      showSuccess,
      handleAutomaticFunctionCall,
      setAiPersonalityMode,
      safeSetTimeout,
      safeClearTimeout,
      todosRef,
      projectsRef,
    ]
  );

  return {
    overwhelmAssessment,
    recentUserMessages,
    checkForOverwhelm,
    setOverwhelmAssessment,
    setRecentUserMessages,
  };
};
